<template>
    <section class="form-embed" :class="{ 'is-open': open }">
        <button
            type="button"
            class="form-embed-toggle"
            :aria-expanded="open ? 'true' : 'false'"
            @click="open = !open"
        >
            <div class="form-embed-heading">
                <h2>{{ t('forms-embed-title') }}</h2>
                <p>{{ t('forms-embed-subtitle') }}</p>
            </div>
            <Icon
                :icon-class="open ? 'ui-chevron-up' : 'ui-chevron-down'"
                size="4"
            />
        </button>

        <div class="form-embed-body" v-show="open">
            <KvTabs v-model="activeTab">
                <template #tabs>
                    <KvTab value="link">{{ t('forms-embed-tab_link') }}</KvTab>
                    <KvTab value="iframe">{{ t('forms-embed-tab_iframe') }}</KvTab>
                    <KvTab value="js">{{ t('forms-embed-tab_js') }}</KvTab>
                </template>
                <template #panels>
                    <KvTabPanel value="link">
                        <div class="embed-panel">
                            <p class="embed-help">{{ t('forms-embed-link_help') }}</p>
                            <embed-snippet :value="directLink" single-line />
                        </div>
                    </KvTabPanel>

                    <KvTabPanel value="iframe">
                        <div class="embed-panel">
                            <p class="embed-lead">{{ t('forms-embed-iframe_lead') }}</p>
                            <p class="embed-step">{{ t('forms-embed-sdk_step') }}</p>
                            <p class="embed-help">{{ t('forms-embed-sdk_help') }}</p>
                            <embed-snippet
                                :label="t('forms-embed-sdk_snippet_label')"
                                :value="sdkSnippet"
                            />
                            <p class="embed-step">{{ t('forms-embed-iframe_step') }}</p>
                            <p class="embed-help">{{ t('forms-embed-iframe_help') }}</p>
                            <embed-snippet
                                :label="t('forms-embed-iframe_snippet_label')"
                                :value="iframeSnippet"
                            />
                        </div>
                    </KvTabPanel>

                    <KvTabPanel value="js">
                        <div class="embed-panel">
                            <p class="embed-lead">{{ t('forms-embed-js_lead') }}</p>
                            <p class="embed-help">{{ t('forms-embed-js_help') }}</p>
                            <embed-snippet
                                :label="t('forms-embed-js_snippet_label')"
                                :value="jsSnippet"
                            />

                            <div class="embed-notes">
                                <p>{{ t('forms-embed-js_note_omit') }}</p>
                                <p>{{ t('forms-embed-js_note_validate') }}</p>
                                <p>{{ t('forms-embed-js_note_response') }}</p>
                            </div>

                            <div class="embed-questions" v-if="answerableQuestions.length">
                                <h3>{{ t('forms-embed-questions_title') }}</h3>
                                <p class="embed-help">{{ t('forms-embed-questions_help') }}</p>
                                <div
                                    class="embed-question-row"
                                    v-for="item in questionReference"
                                    :key="item.id"
                                >
                                    <div class="embed-question-main">
                                        <!-- i18n-validator-ignore-next-line: runtime question text -->
                                        <span class="embed-question-text">{{ item.label }}</span>
                                        <span class="embed-question-type">{{ item.typeLabel }}</span>
                                    </div>
                                    <button
                                        type="button"
                                        class="embed-question-id"
                                        :title="t('forms-questions-copy_id')"
                                        :aria-label="t('forms-questions-copy_id')"
                                        @click="copyId(item.id)"
                                    >
                                        <Icon
                                            :icon-class="copiedId === item.id ? 'ui-check' : 'ui-copy-03'"
                                            size="2"
                                        />
                                        <!-- i18n-validator-ignore-next-line: runtime question id -->
                                        <span>{{ item.id }}</span>
                                    </button>
                                    <span class="embed-question-format">{{ item.format }}</span>
                                </div>
                            </div>
                            <p class="embed-help" v-else>
                                {{ t('forms-embed-questions_empty') }}
                            </p>
                        </div>
                    </KvTabPanel>
                </template>
            </KvTabs>
        </div>
    </section>
</template>

<script>
import Icon from "../../components/icon/icon";
import { htmlToText } from "../../helpers/html";
import KvTabs from "../../components/tabs/kv-tabs.vue";
import KvTab from "../../components/tabs/kv-tab.vue";
import KvTabPanel from "../../components/tabs/kv-tab-panel.vue";
import EmbedSnippet from "./embed-snippet.vue";

const TYPE_FORMAT_KEYS = {
    emotion: "forms-embed-format_emotion",
    nps: "forms-embed-format_nps",
    ces: "forms-embed-format_ces",
    boolean: "forms-embed-format_boolean",
    selectbox: "forms-embed-format_selectbox",
    input: "forms-embed-format_input",
    comment: "forms-embed-format_comment",
    date: "forms-embed-format_date",
    file: "forms-embed-format_file",
};

const EXAMPLE_VALUES = {
    emotion: "4",
    nps: "9",
    ces: "5",
    boolean: '"1"',
    selectbox: '["Option A"]',
    input: '"Short answer"',
    comment: '"Longer comment"',
    date: '"2026-03-15"',
    file: '[{"url":"https://example.com/file.pdf","name":"file.pdf","type":"application/pdf","size":1.2}]',
};

export default {
    name: "FormEmbed",
    components: {
        Icon,
        KvTabs,
        KvTab,
        KvTabPanel,
        "embed-snippet": EmbedSnippet,
    },
    props: {
        formId: {
            type: [String, Number],
            required: true,
        },
        questions: {
            type: Array,
            default: () => [],
        },
        askSettings: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            open: false,
            activeTab: "link",
            copiedId: null,
            copiedTimeout: null,
        };
    },
    computed: {
        place() {
            return this.$store.state.place || {};
        },
        directLink() {
            return `${window.baseUrl}#/form/${this.place.slug}/${this.formId}`;
        },
        embedLink() {
            return `${this.directLink}?embed=1`;
        },
        apiAnswersUrl() {
            const base = (window.apiEndPoint || "https://api.kevi.io/").replace(/\/?$/, "/");
            return `${base}answers`;
        },
        answerableQuestions() {
            return (this.questions || []).filter(
                (question) =>
                    !question.removed &&
                    question.type !== "content" &&
                    !(typeof question.id === "string" && question.id.startsWith("new-")),
            );
        },
        questionReference() {
            return this.answerableQuestions.map((question) => ({
                id: String(question.id),
                label: this.plainText(question.question),
                typeLabel: this.t(`forms-questions-type_${question.type}`),
                format: this.t(
                    TYPE_FORMAT_KEYS[question.type] || "forms-embed-format_input",
                ),
                type: question.type,
                options: (question.options || [])
                    .filter((option) => !option.removed)
                    .map((option) => option.text)
                    .filter(Boolean),
            }));
        },
        iframeSnippet() {
            return [
                `<iframe`,
                `  src="${this.embedLink}"`,
                `  width="100%"`,
                `  height="600"`,
                `  style="border:0;background:transparent;"`,
                `></iframe>`,
            ].join("\n");
        },
        sdkSnippet() {
            return [
                `<script src="https://assets.kevi.io/sdk/kevi.bundle.latest.js" async><\/script>`,
                `<script>`,
                `  kevi.init("${this.place.id}");`,
                `  kevi.onFormSubmit(function (result) {`,
                `    // result.answer, result.coupon, result.client`,
                `    console.log(result);`,
                `  }, "${this.formId}");`,
                `<\/script>`,
            ].join("\n");
        },
        jsSnippet() {
            const userLines = this.userSnippetLines();
            const questionLines = this.questionSnippetLines();
            const body = [`    forms_id: "${this.formId}",`];
            if (userLines.length) {
                body.push(`    user: {`);
                userLines.forEach((line) => body.push(`      ${line}`));
                body.push(`    },`);
            }
            body.push(`    steps_questions: {`);
            if (questionLines.length) {
                questionLines.forEach((line) => body.push(`      ${line}`));
            } else {
                body.push(`      // add question answers here`);
            }
            body.push(`    }`);

            return [
                `fetch("${this.apiAnswersUrl}", {`,
                `  method: "PUT",`,
                `  headers: {`,
                `    "Content-Type": "application/json",`,
                `    Accept: "application/json"`,
                `  },`,
                `  body: JSON.stringify({`,
                ...body,
                `  })`,
                `})`,
                `  .then((response) => response.json())`,
                `  .then((data) => {`,
                `    // data.answer, data.coupon, data.client`,
                `    if (window.kevi && data.client) {`,
                `      kevi.setClient(data.client);`,
                `    }`,
                `  });`,
            ].join("\n");
        },
    },
    beforeDestroy() {
        clearTimeout(this.copiedTimeout);
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        plainText(value) {
            return htmlToText(value).replace(/\s+/g, " ").trim();
        },
        userSnippetLines() {
            const settings = this.askSettings || {};
            const lines = [];
            if (settings.askName && settings.askName !== "no") {
                lines.push(`first_name: "Maria",`);
            }
            if (settings.askLastName && settings.askLastName !== "no") {
                lines.push(`last_name: "Silva",`);
            }
            if (settings.askEmail && settings.askEmail !== "no") {
                lines.push(`email: "maria@example.com",`);
            }
            if (settings.askPhone && settings.askPhone !== "no") {
                lines.push(`whatsapp: "48999999999",`);
            }
            if (settings.askBirthdate && settings.askBirthdate !== "no") {
                lines.push(`birthdate: "15/03/1990",`);
            }
            if (lines.length) {
                const last = lines[lines.length - 1];
                lines[lines.length - 1] = last.replace(/,$/, "");
            }
            return lines;
        },
        questionSnippetLines() {
            return this.questionReference.map((item, index) => {
                let example = EXAMPLE_VALUES[item.type] || '""';
                if (item.type === "selectbox" && item.options.length) {
                    const sample = item.options.slice(0, item.options.length > 1 ? 2 : 1);
                    example = JSON.stringify(sample);
                }
                const comment = `${item.label || item.typeLabel} (${item.format})`;
                const comma = index < this.questionReference.length - 1 ? "," : "";
                return `"${item.id}": ${example}${comma} // ${comment}`;
            });
        },
        copyId(questionId) {
            const value = String(questionId);
            const done = () => {
                this.copiedId = questionId;
                clearTimeout(this.copiedTimeout);
                this.copiedTimeout = setTimeout(() => {
                    this.copiedId = null;
                }, 2000);
                this.$store.dispatch(
                    "notification/success",
                    this.t('forms-questions-id_copied'),
                );
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(value).then(done, done);
                return;
            }
            const helper = document.createElement("textarea");
            helper.value = value;
            document.body.appendChild(helper);
            helper.select();
            document.execCommand("copy");
            document.body.removeChild(helper);
            done();
        },
    },
};
</script>

<style scoped lang="less">
.form-embed {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
}

.form-embed-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: 16px 20px;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: #f9fafb;
    }
}

.form-embed-heading {
    min-width: 0;

    h2 {
        margin: 0 0 4px;
        font-size: 15px;
        font-weight: 500;
        color: #111927;
    }

    p {
        margin: 0;
        font-size: 12px;
        line-height: 1.45;
        color: #6c737f;
    }
}

.form-embed-body {
    padding: 0 20px 20px;
    border-top: 1px solid #f3f4f6;
}

.embed-panel {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.form-embed-body ::v-deep .kv-tab-panel {
    margin-top: 12px;
}

.embed-step {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    color: #111927;
}

.embed-lead {
    margin: 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f1fcf3;
    border: 1px solid #d3f0db;
    font-size: 13px;
    line-height: 1.5;
    color: #1d7836;
}

.embed-help {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: #6c737f;
}

.embed-notes {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #f9fafb;
    border: 1px solid #f3f4f6;

    p {
        margin: 0;
        font-size: 12px;
        line-height: 1.45;
        color: #4d5761;
    }
}

.embed-questions {
    display: flex;
    flex-direction: column;
    gap: 10px;

    h3 {
        margin: 0;
        font-size: 14px;
        font-weight: 500;
        color: #111927;
    }
}

.embed-question-row {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) auto minmax(0, 1fr);
    gap: 10px;
    align-items: center;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;

    @media screen and (max-width: 767px) {
        grid-template-columns: 1fr;
        align-items: flex-start;
    }
}

.embed-question-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.embed-question-text {
    font-size: 13px;
    color: #111927;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.embed-question-type {
    font-size: 11px;
    color: #9da4ae;
}

.embed-question-id {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    background: #fff;
    color: #6c737f;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    line-height: 18px;

    &:hover {
        border-color: #2aaa4c;
        color: #1d7836;
    }
}

.embed-question-format {
    font-size: 12px;
    color: #4d5761;
}
</style>
